import React, { useState, useMemo } from 'react';
import './GardenFresh.css';
import { paginateMenu } from '../../../utils/menuPagination';
import { exportPtPagesToPdf } from '../../../utils/exportPtPagesToPdf';
import ExportPdfFab from '../../../components/ExportPdfFab';

const GardenFresh = ({ data, isSingleMenu }) => {
    const restaurant = data?.restaurant || {};
    const rawMenus = data?.menus || [];

    const [activeMenuIndex, setActiveMenuIndex] = useState(0);

    // Determine which pages to display
    const visiblePages = useMemo(() => {
        if (isSingleMenu) {
            return rawMenus.flatMap(menu => paginateMenu(menu));
        }
        const activeMenu = rawMenus[activeMenuIndex];
        return activeMenu ? paginateMenu(activeMenu) : [];
    }, [isSingleMenu, rawMenus, activeMenuIndex]);

    const handleNext = () => {
        if (activeMenuIndex < rawMenus.length - 1) {
            setActiveMenuIndex(p => p + 1);
            window.scrollTo(0, 0);
        }
    };

    const handlePrev = () => {
        if (activeMenuIndex > 0) {
            setActiveMenuIndex(p => p - 1);
            window.scrollTo(0, 0);
        }
    };

    if (!data) return <div className="loading">Loading...</div>;

    return (
        <div className="gf-root">
            {/* Navigation - Only show in Multi-Menu Mode (Preview) */}
            {!isSingleMenu && (
                <div className="tmpl-nav-container no-print" style={{ justifyContent: 'center' }}>
                    <div className="tmpl-nav-controls">
                        <button className="tmpl-nav-btn" onClick={handlePrev} disabled={activeMenuIndex === 0}>
                            ← Prev Menu
                        </button>
                        <div className="tmpl-page-indicator">
                            {rawMenus[activeMenuIndex]?.name || `Menu ${activeMenuIndex + 1}`}
                            <span style={{ fontSize: '0.8em', opacity: 0.7, marginLeft: '8px' }}>
                                ({activeMenuIndex + 1}/{rawMenus.length})
                            </span>
                        </div>
                        <button className="tmpl-nav-btn" onClick={handleNext} disabled={activeMenuIndex === rawMenus.length - 1}>
                            Next Menu →
                        </button>
                    </div>
                </div>
            )}

            <ExportPdfFab onClick={() => exportPtPagesToPdf({ filename: `${restaurant.name || 'menu'}-garden-fresh.pdf` })} />

            <div className="gf-container">
                {visiblePages.map((page, index) => {
                    const categories = (page.categories || []);

                    // Header logic:
                    // PDF: Only on very first page
                    // Preview: On first page of the active menu
                    const showHeader = isSingleMenu ? (index === 0) : (page.chunkIndex === 0);

                    // Menu Title Logic: Only show on first page of the menu chunk
                    const showMenuTitle = page.chunkIndex === 0;

                    return (
                        <div key={`${page.id || page.name}-${index}`} className="gf-page pt-page active">
                            <div className="gf-leaf-accent top-right">🌿</div>
                            <div className="gf-leaf-accent bottom-left">🍃</div>

                            {showHeader && (
                                <header className="gf-header">
                                    <h1 className="gf-name">{restaurant.name}</h1>
                                    {restaurant.tagline && <div className="gf-tagline">{restaurant.tagline}</div>}
                                    <div className="gf-divider"></div>
                                    <div className="gf-meta">{restaurant.address} • {restaurant.phoneNumber}</div>
                                </header>
                            )}

                            <div className="gf-body">
                                {showMenuTitle && (
                                    <h2 className="gf-menu-title">{page.name}</h2>
                                )}
                                <div className="gf-grid">
                                    {categories.map(category => (
                                        <div key={category.id} className="gf-category">
                                            <h3 className="gf-category-title">{category.name}</h3>
                                            <div className="gf-items">
                                                {(category.items || []).map(item => (
                                                    <div key={item.id} className="gf-item">
                                                        <div className="gf-item-row">
                                                            <div className="gf-item-name-group">
                                                                {(item.isVeg !== undefined && item.isVeg !== null) && (
                                                                    <span className={`gf-veg-badge ${item.isVeg ? 'veg' : 'nonveg'}`}>
                                                                        <span className="gf-veg-mark"></span>
                                                                    </span>
                                                                )}
                                                                <span className="gf-item-name">{item.name}</span>
                                                            </div>
                                                            <span className="gf-item-price">₹{item.price}</span>
                                                        </div>
                                                        {item.description && <p className="gf-item-desc">{item.description}</p>}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <footer className="gf-footer">
                                {restaurant.footerText && <p>{restaurant.footerText}</p>}
                            </footer>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default GardenFresh;
